import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import OnboardEmployee from './OnboardEmployee';

const forms = {
  offerLetter: { name: "Offer Letter", type: "Signed document", required: true },
  personalInformation: { name: "Personal Information", type: "HTML", required: true },
  emergencyContact: { name: "Emergency Contact", type: "HTML", required: true },
  atWillEmployment: { name: "At-Will Employment Form", type: "HTML", required: true },
  handbookAgreement: {
    name: "Handbook Agreement",
    type: ["Signed document", "HTML"],
    required: true,
  },
  directDeposit: {
    name: "Direct Deposit Form",
    type: "HTML",
    required: false,
    additionalUpload: {
      required: true,
      description: "Ability to upload a check or bank authorization form",
    },
  },
  nyIT2104Form: { name: "NY IT-2104 Form", type: "Signed document", required: true },
  wageRateNotice: { name: "Wage Rate Notice Form", type: "Signed document", custom: true, required: true },
  w4Form: { name: "W4 Form", type: "Signed document", required: true },
  harassmentTraining: {
    name: "Annual Mandatory Harassment Workplace Training",
    type: "To be discussed",
    required: true,
  },
  foodHandlersCertification: {
    name: "Food Handlers Certification",
    type: "Optional upload",
    additionalUpload: {
      required: true,
      description: "Ability to upload food handlers certification",
    },
  },
};

const FormsPage = ({ mode = "full", formsStatus = {} }) => {
  const [selectedForms, setSelectedForms] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOnboardingModalOpen, setIsOnboardingModalOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const onboardingForms = Object.keys(forms);

  const toggleFormSelection = (formKey) => {
    setSelectedForms((prev) =>
      prev.includes(formKey)
        ? prev.filter((key) => key !== formKey)
        : [...prev, formKey]
    );
  };

  const openSendModal = () => setIsModalOpen(true);
  const closeSendModal = () => {
    setIsModalOpen(false);
    setRecipientEmail('');
    setSelectedForms([]);
  };

  const openOnboardingModal = () => setIsOnboardingModalOpen(true);
  const closeOnboardingModal = () => {
    setIsOnboardingModalOpen(false);
    setRecipientEmail('');
  };

  const handleSaveEmployee = (employeeInfo, selectedForms) => {
    console.log('New Employee:', employeeInfo);
    console.log('Selected Forms:', selectedForms);
  };

  const handleSendForms = (isOnboarding = false) => {
    const formsToSend = isOnboarding ? onboardingForms : selectedForms;
    console.log(
      `Sending forms ${formsToSend
        .map((key) => forms[key].name)
        .join(', ')} to ${recipientEmail}`
    );
    isOnboarding ? closeOnboardingModal() : closeSendModal();
  };

  // If in "status" mode, just show status...
  if (mode === "status") {
    return (
      <Box
        sx={{
          p: 2,
          maxWidth: '100%',
          mx: 'auto',
          backgroundColor: '#C2E8F7',
          border: '1px solid #1976d2',
          borderRadius: '8px',
          mb: 2,
        }}
      >
        {/* Status layout as before */}
      </Box>
    );
  }

  return (
    <>
      {/* Main Content */}
      
      
      <Box sx={{ p: 2, mx: 'auto', pb: 8, marginBottom: 8 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={5} mt={2}>
          <Typography variant="h4" color="black" sx={{ fontWeight: 'bold', textAlign: 'center', margin: '0 auto' }}>
            Employee Forms
          </Typography>
        </Box>
          
          <Button sx={{ mb: 2 }} variant="contained" color="primary" onClick={openOnboardingModal}>
            Onboard Employee
          </Button>

        <Grid container spacing={1}>
          {Object.entries(forms).map(([key, form]) => {
            const isSelected = selectedForms.includes(key);
            return (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Paper
    onClick={() => toggleFormSelection(key)}
    sx={{
      p: 1.5,
      display: 'flex',
      flexDirection: 'column',
      height: {
        xs: 100, // shorter on mobile
        sm: 150, // taller on small+ screens
      },
      justifyContent: 'space-between',
      border: isSelected ? '2px solid #1976d2' : '1px solid #ddd',
      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
      cursor: 'pointer', // show it's clickable
    }}
  >
    {/* Row with checkbox + title */}
    <Box display="flex" alignItems="center">
      <Checkbox
        checked={isSelected}
        color="primary"
        sx={{ mr: 1 }} 
        // no onChange here
      />
      <Typography variant="h6" sx={{ fontSize: '1rem' }}>
        {form.name}
      </Typography>
    </Box>

    <Typography variant="body2" color="text.secondary">
      Type: {Array.isArray(form.type) ? form.type.join(', ') : form.type}
    </Typography>
    {form.additionalUpload && (
      <Typography variant="body2" color="info.main">
        {form.additionalUpload.description}
      </Typography>
    )}
  </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Fixed "Send Selected" Button at Bottom */}
      {selectedForms.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            width: '100%',
            p: 2,
            backgroundColor: '#fff',
            borderTop: '1px solid #ccc',
            boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.1)',
            zIndex: 999,
          }}
        >
          <Box
            sx={{
              maxWidth: 1000,
              mx: 'auto',
              display: 'flex',
              justifyContent: {
                xs: 'center',   // center on mobile
                sm: 'flex-end'  // right align on small+ screens
              },
            }}
          >
            <Button variant="contained" color="primary" onClick={openSendModal}>
              Send Selected ({selectedForms.length})
            </Button>
          </Box>
        </Box>
      )}

      {/* Send Selected Forms Modal */}
      <Dialog open={isModalOpen} onClose={closeSendModal} maxWidth="sm" fullWidth>
        <DialogTitle>Send Selected Forms</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Selected forms:
            <ul>
              {selectedForms.map((key) => (
                <li key={key}>{forms[key].name}</li>
              ))}
            </ul>
          </Typography>
          <TextField
            label="Recipient Name"
            variant="outlined"
            fullWidth
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Recipient Email"
            variant="outlined"
            fullWidth
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSendModal} color="secondary">
            Cancel
          </Button>
         
        </DialogActions>
      </Dialog>

      {/* Onboard Employee Modal */}
      <Dialog
        open={isOnboardingModalOpen}
        onClose={closeOnboardingModal}
        fullScreen={isMobile} // full-screen on mobile
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <OnboardEmployee onSave={handleSaveEmployee} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOnboardingModal} color="secondary">
            Cancel
          </Button>
       
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FormsPage;